import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CookieConsentProvider } from '@/contexts/CookieConsentContext';
import CookieConsentBanner from '@/components/common/CookieConsentBanner';
import MobileMenu from '@/components/common/MobileMenu';
import { useCookieConsent } from '@/contexts/CookieConsentContext';

// Component for cookie preferences link in footer (must be inside CookieConsentProvider)
const CookiePreferencesLink: React.FC = () => {
  const { showBanner } = useCookieConsent();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    showBanner();
  };

  return (
    <li>
      <button
        onClick={handleClick}
        className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm"
      >
        Cookie Preferences
      </button>
    </li>
  );
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <CookieConsentProvider>
      <div className="min-h-screen flex flex-col">
        {/* Skip to Content Link for Accessibility */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:font-medium"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById('main-content')?.focus();
            document.getElementById('main-content')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Skip to main content
        </a>

        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="container-custom">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-2">
                 <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                   <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M9 7h12M12 3v8" />
                   </svg>
                 </div>
                <span className="text-xl font-bold text-gray-900">ChinaHospital</span>
              </Link>

              <nav className="hidden md:flex items-center space-x-6">
                <Link to="/hospitals" className="text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors">
                  Hospitals
                </Link>
                <Link to="/hospitals/ranking" className="text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors">
                  Hospital Rankings
                </Link>
                <div className="relative group">
                  <button className="text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors flex items-center gap-1">
                    Visa Guide
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7 7" />
                    </svg>
                  </button>
                  <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <div className="py-2">
                      <Link
                        to="/visa-guide"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-primary-600 transition-colors"
                      >
                        Visa-Free Entry
                      </Link>
                      <Link
                        to="/visa/medical"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-primary-600 transition-colors"
                      >
                        Medical Visa Guide
                      </Link>
                    </div>
                  </div>
                </div>
                <Link to="/guides" className="text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors">
                  Guides
                </Link>
                <Link to="/services" className="text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors">
                  Services
                </Link>
                <Link to="/contact" className="text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors">
                  Contact Us
                </Link>
              </nav>

              <div className="hidden md:flex items-center space-x-3">
                <Link to="/services" className="btn-primary text-sm">
                  Get Started
                </Link>
              </div>

              <button
                className="md:hidden min-h-[44px] min-w-[44px] p-2 rounded-md hover:bg-gray-100"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle mobile menu"
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </header>

        <main id="main-content" className="flex-grow py-8" tabIndex={-1}>
          {children}
        </main>

        <footer className="bg-gray-900 text-white">
          <div className="container-custom py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="col-span-1 md:col-span-2">
                <div className="flex items-center space-x-2 mb-4">
                   <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                     <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M12 21v-8" />
                     </svg>
                   </div>
                  <span className="text-xl font-bold">ChinaHospital</span>
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  Fast access to China's top-tier hospitals with visa-free entry. We provide consultation, booking assistance and hassle-free medical tourism experiences.
                </p>
                <p className="text-gray-500 text-xs">
                  © 2024-2026 ChinaHospital. All rights reserved.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link to="/hospitals" className="text-gray-400 hover:text-white transition-colors">
                      Hospital Directory
                    </Link>
                  </li>
                  <li>
                    <Link to="/hospitals/ranking" className="text-gray-400 hover:text-white transition-colors">
                      Hospital Rankings
                    </Link>
                  </li>
                  <li>
                    <Link to="/visa-guide" className="text-gray-400 hover:text-white transition-colors">
                      Visa-Free Entry
                    </Link>
                  </li>
                  <li>
                    <Link to="/visa/medical" className="text-gray-400 hover:text-white transition-colors">
                      Medical Visa Guide
                    </Link>
                  </li>
                  <li>
                    <Link to="/guides" className="text-gray-400 hover:text-white transition-colors">
                      Guides Hub
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-gray-400 hover:text-white transition-colors">
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link to="/services" className="text-gray-400 hover:text-white transition-colors">
                      Services & Pricing
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
                      Contact Us
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-4">Legal</h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link to="/disclaimer" className="text-gray-400 hover:text-white transition-colors">
                      Disclaimer
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                      Privacy Policy
                    </Link>
                  </li>
                  <CookiePreferencesLink />
                </ul>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-gray-800">
              <p className="text-gray-500 text-xs text-center">
                Disclaimer: This website provides information services and administrative assistance only. We do not provide medical diagnosis or treatment. All medical services are provided by the hospitals listed.
              </p>
            </div>
          </div>
        </footer>

        <CookieConsentBanner />
      </div>
    </CookieConsentProvider>
  );
};

export default Layout;
